import {
    Alert,
    Box,
    Button, Fab, Grid, IconButton, Paper,
    Tooltip, Typography
} from '@mui/material';
import * as React from 'react';
import { Link as RouterLink, Navigate, useNavigate, useParams } from 'react-router-dom';
import Headline from '../../shared/components/Headline';
import { useCallback, useEffect, useState } from 'react';
import {
    Chatbot,
    TrainingStatus,
    botIsTraining,
} from '../../model/Chatbot';
import { PiUsersBold } from "react-icons/pi";
import { useChatbotApiClient } from '../../clients/ChatbotApiClient';
import GlobalLoader from '../../shared/components/GlobalLoader';
import { Add, Settings } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import TrainingStatusIcon from './components/TrainingStatusIcon';
import DisabledToolTip from '../../shared/components/DisabledToolTip';
import { useTranslation } from 'react-i18next';
import SettingsModal from './SettingsModal';
import UserSettingsModal from './UserSettingsModal';
import AgentSkillMarketplaceModal from './components/AgentSkillMarketplaceModal';
import AgentSkillGrid from './components/AgentSkillGrid';
import {AGENT_SKILL_TYPES, AgentSkill, AgentSkillType} from './types/AgentSkillTypes';
import AgentSkillSettingsModal from './components/AgentSkillSettingsModal';
import QRCodeLink from '../chatbot/components/QRCodeLink';
import {ErrorResponse} from "../../clients/error/ErrorResponse";


export interface IMyChatBotProps {
}

const MyChatBot: React.FunctionComponent<IMyChatBotProps> = (props: IMyChatBotProps) => {
    const { botId } = useParams()
    // const theme = useTheme();
    // const userCtx = useUserContext()
    const navigate = useNavigate()
    const { t }: any = useTranslation();
    const chatbotClient = useChatbotApiClient()

    
    const [bot, setBot] = useState<Chatbot>()
    const [settingsOpen, setsettingsOpen] = useState(false)
    const [savingLoading, setSavingLoading] = useState(false)
    const [pageError, setPageError] = useState<string>()
    const [userSettingsOpen, setUserSettingsOpen] = useState(false)
    const fetchBotInterval = React.useRef<NodeJS.Timeout | null>(null)
    
    const [marketplaceOpen, setMarketplaceOpen] = useState(false);
    const [agentSkills, setAgentSkills] = useState<AgentSkill[]>([]);
    const [selectedSkill, setSelectedSkill] = useState<AgentSkill | null>(null);
    const [settingsModalOpen, setSettingsModalOpen] = useState(false);

    const fetchChatbot = useCallback(() => {
        if (botId) {
            chatbotClient.getChatbot(botId)
                .then(bot => {
                    setBot(bot)
                    
                    if (!fetchBotInterval.current) {
                        if (botIsTraining(bot)) {
                            fetchBotInterval.current = setInterval(() => fetchChatbot(), 2000)
                        }
                    } else {
                        if (!botIsTraining(bot)) {
                            clearInterval(fetchBotInterval.current || undefined)
                            fetchBotInterval.current = null
                        }
                    }
                })
                .catch(() => { navigate('/chatBots') })
        }
    }, [botId, chatbotClient, navigate])

    useEffect(() => {
        fetchChatbot()
    }, [fetchChatbot])
    
    
    // Load data sources from backend when API is ready
    useEffect(() => {
        if (bot) {
            
            if (!bot.settings?.agentSkills?.length) {
                // init default agent skills
                const defaultSkills = []
                const llm_base_knowlege_skill: AgentSkill = {
                    id: Math.random().toString(36).substr(2, 9),
                    type: AgentSkillType.LLM_BASE_KNOWLEDGE,
                    name: AGENT_SKILL_TYPES[AgentSkillType.LLM_BASE_KNOWLEDGE].name,
                    description: 'tbd',
                    status: 'active',
                    configuration: {
                        skill_name: AgentSkillType.LLM_BASE_KNOWLEDGE,
                        user_skill_description: "",
                    },
                    lastUpdated: new Date(),
                };
                defaultSkills.push(llm_base_knowlege_skill)
                
                const conv_history_skill: AgentSkill = {
                    id: Math.random().toString(36).substr(2, 9),
                    type: AgentSkillType.CONVERSATION_HISTORY,
                    name: AGENT_SKILL_TYPES[AgentSkillType.CONVERSATION_HISTORY].name,
                    description: 'tbd',
                    status: 'active',
                    configuration: {
                        skill_name: AgentSkillType.CONVERSATION_HISTORY,
                        user_skill_description: "",
                    },
                    lastUpdated: new Date(),
                };
                defaultSkills.push(conv_history_skill)
                
                const datetime_skill: AgentSkill = {
                    id: Math.random().toString(36).substr(2, 9),
                    type: AgentSkillType.DATE_AND_TIME,
                    name: AGENT_SKILL_TYPES[AgentSkillType.DATE_AND_TIME].name,
                    description: 'tbd',
                    status: 'active',
                    configuration: {
                        skill_name: AgentSkillType.DATE_AND_TIME,
                        user_skill_description: "",
                        timezone: 'Europe/Berlin',
                    },
                    lastUpdated: new Date(),
                };
                defaultSkills.push(datetime_skill)
                
                if (bot.settings.web_rag_city !== "") {

                    const rtRAG_skill: AgentSkill = {
                        id: Math.random().toString(36).substr(2, 9),
                        type: AgentSkillType.RT_RAG,
                        name: AGENT_SKILL_TYPES[AgentSkillType.RT_RAG].name,
                        description: 'tbd',
                        status: 'active',
                        configuration: {
                            skill_name: AgentSkillType.RT_RAG,
                            user_skill_description: "",
                            web_rag_city: bot.settings.web_rag_city,
                            web_rag_base_url: bot.settings.web_rag_base_url,
                            webRagSelectorsStr: bot.settings.web_rag_selectors,
                            web_rag_max_search_results: bot.settings.web_rag_max_search_results,
                        },
                        lastUpdated: new Date(),
                    };
                    defaultSkills.push(rtRAG_skill)
                }
                
                setAgentSkills(defaultSkills);
                
            } else {
                setAgentSkills(bot.settings.agentSkills)
            }
        }
    }, [bot]);

    const handleSaveAgentSkill = async (type: AgentSkillType, configuration: Record<string, any>) => {
        // TODO: Implement API call to create data source
        // POST /api/bot/{botId}/datasources
        
        // Placeholder: Create new source locally
        const newSkill: AgentSkill = {
            id: Math.random().toString(36).substr(2, 9),
            type,
            name: AGENT_SKILL_TYPES[type].name,
            description: 'tbd',
            status: 'active',
            configuration,
            lastUpdated: new Date()
        };
        setAgentSkills(prev => [...prev, newSkill]);
        
        
        
    };

    const handleUpdateAgentSkill = async (configuration: Record<string, any>) => {
        if (!selectedSkill) return;
        
        // TODO: Implement API call to update data source
        // PUT /api/bot/{botId}/datasources/{sourceId}
        
        // Placeholder: Update source locally
        setAgentSkills(prev => prev.map(s => 
            s.id === selectedSkill.id 
                ? { ...s, configuration, status: 'inactive', lastUpdated: new Date() }
                : s
        ));
        setSelectedSkill(null);
    };

    const handleConfigureAgentSkill = (skill: AgentSkill) => {
        setSelectedSkill(skill);
        setSettingsModalOpen(true);
    };

    const handleDeleteAgentSkill = async (skill: AgentSkill) => {
        // TODO: Implement API call to delete data source
        // DELETE /api/bot/{botId}/datasources/{sourceId}
        
        // Placeholder: Remove source locally
        setAgentSkills(prev => prev.filter(s => s.id !== skill.id));
    };

    const saveSettings = () => {
        if (bot && botId) {
            setSavingLoading(true)
            const bot_settings_copy = JSON.parse(JSON.stringify(bot.settings))
            bot_settings_copy.agentSkills = agentSkills
            chatbotClient.setChatbotSettings(botId, bot_settings_copy)
                .catch((err: ErrorResponse) => {
                    debugger;
                    setPageError(err.message)
                })
                .finally(() => setSavingLoading(false))
        }
    }

    if (!botId) {
        return <Navigate to={'/chatBots'} />
    }

    if (!bot) {
        return <GlobalLoader />
    }


    return (
        <>
            <Grid container justifyContent='center' spacing={2}>
                {pageError &&
                    <Grid container spacing={2} justifyContent={"center"}>
                        <Grid item xs={12} container justifyContent='center'>
                            <Alert severity="error">
                                {pageError}
                            </Alert>
                        </Grid>
                    </Grid>
                }
                <Grid item xs={12}>
                    <Typography variant="h5" align="center">
                        {t('chatbot.myBot', { botName: bot.name })}
                    </Typography>
                    <Headline title={bot.name} />
                </Grid>
                <Grid item xs={12}>
                    <Typography variant="h5" align="center" color="error">{bot.description}</Typography>
                </Grid>
                <Grid item lg={8} md={10} xs={12} >
                    <Paper>
                        <Grid container sx={{ padding: "1em", overflow: 'auto' }}>
                            <Grid item xs={12} container spacing={1} alignItems="center">
                                <Grid item xs={6} container spacing={3} alignItems="center">
                                    <Grid item>
                                        <Typography variant="h5" >{t('chatbot.trainingStatus.label')}</Typography>
                                    </Grid>
                                    <Grid item>
                                        <TrainingStatusIcon status={bot.trainingStatus} />
                                    </Grid>
                                </Grid>
                                <Grid item xs={6} spacing={2} container justifyContent="flex-end">
                                    <Grid item>
                                        <Tooltip title={t('chatbot.settings')}>
                                            <IconButton onClick={() => setsettingsOpen(true)}>
                                                <Settings />
                                            </IconButton>
                                        </Tooltip>
                                    </Grid>
                                    <Grid item>
                                        <Tooltip title={"Users"}>
                                            <IconButton onClick={() => setUserSettingsOpen(true)}>
                                                <PiUsersBold />
                                            </IconButton>
                                        </Tooltip>
                                    </Grid>
                                    <Grid item>
                                        <DisabledToolTip tooltip={"Statistics"}>
                                            <Button color="secondary" variant="contained" component={RouterLink} to={`/bot/${bot.id}/stats`}>
                                                Statistics
                                            </Button>
                                        </DisabledToolTip>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>

                <Grid item lg={8} md={10} xs={12} sx={{marginTop: '15px', marginBottom: '15px'}}>
                    <Grid item xs={12} container spacing={1} alignItems="center">
                        <Grid item xs={10}>
                            <Typography variant="h5">{"AI-Agent Skills:"}</Typography>
                        </Grid>
                        <Grid item xs={2} container justifyContent="flex-end">
                            <Fab color="primary" onClick={() => setMarketplaceOpen(true)}>
                                <Add />
                            </Fab>
                        </Grid>

                        <Grid item xs={12}>
                            <AgentSkillGrid
                                sources={agentSkills}
                                onConfigureSource={handleConfigureAgentSkill}
                                onDeleteSource={handleDeleteAgentSkill}
                            />
                        </Grid>
                        <Grid item xs={12} sx={{marginTop: '15px'}}>
                            <Box display="flex" justifyContent="center">
                                <LoadingButton color="error" loading={savingLoading} onClick={() => saveSettings()} variant="contained">
                                    {"Save"}
                                </LoadingButton>
                            </Box>
                        </Grid>
                    </Grid>
                </Grid>

                <Grid item lg={8} md={10} xs={12} sx={{marginTop: '15px', marginBottom: '15px'}}>
                    <Grid item xs={12} container spacing={1} alignItems="center">
                        <Grid item xs={10}>
                            <Typography variant="h5">{"Links:"}</Typography>
                        </Grid>
                        <Grid item xs={2} container justifyContent="flex-end">
                            
                        </Grid>

                        <Grid item xs={12}>
                            <Grid container sx={{ paddingBottom: "1em", overflow: 'auto' }} spacing={1}>

                                <Grid item xs={4}>
                                    <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
                                        <Button disabled={bot.trainingStatus !== TrainingStatus.TRAINED} variant="contained" component={RouterLink} to={`/bot/${bot.id}/chat/iframe/undecorated`} target="_blank" rel="noopener noreferrer">
                                            {t('Chat iFrame')}
                                        </Button>
                                        <QRCodeLink url={`${window.location.origin}/bot/${bot.id}/chat/iframe/undecorated`} showUrl={false} showTitle={false}/>
                                    </Box>
                                </Grid>

                                <Grid item xs={4}>
                                    <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
                                        <Button disabled={bot.trainingStatus !== TrainingStatus.TRAINED} variant="contained" component={RouterLink} to={`/bot/${bot.id}/chat/undecorated`} target="_blank" rel="noopener noreferrer">
                                            {t('Chat Mobile')}
                                        </Button>
                                        <QRCodeLink url={`${window.location.origin}/bot/${bot.id}/chat/undecorated`} showUrl={false} showTitle={false}/>
                                    </Box>
                                </Grid>

                                <Grid item xs={4}>
                                    <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
                                        <Button disabled={bot.trainingStatus !== TrainingStatus.TRAINED} variant="contained" component={RouterLink} to={`/bot/${bot.id}/chat`} target="_blank" rel="noopener noreferrer">
                                            {t('Chat')}
                                        </Button>
                                        <QRCodeLink url={`${window.location.origin}/bot/${bot.id}/chat`} showUrl={false} showTitle={false}/>
                                    </Box>
                                </Grid>

                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
                
                <Grid item lg={8} md={10} xs={12} >
                    
                </Grid>
                
                
                <Grid item lg={8} md={10} xs={12} >

                    <Grid container sx={{ padding: "1em", overflow: 'auto' }}>
                        <hr/>
                    </Grid>
                    <Grid container sx={{ padding: "1em", overflow: 'auto' }}>
                        <Grid item xs={12} container spacing={1} alignItems="center">
                            <Grid item xs={10}>
                                <Typography variant="h5">{"Danger-Zone"}</Typography>
                            </Grid>

                            <Grid item xs={12} spacing={2} container justifyContent="flex-end">
                                <Grid item>
                                    <Tooltip title={"Reset Cache"}>
                                        <Button color="secondary" variant="contained"
                                                onClick={() => chatbotClient.resetCache(botId)}
                                        >
                                            Reset Cache
                                        </Button>
                                    </Tooltip>
                                </Grid>
                                <Grid item>
                                    <Tooltip title={"Reset Cache"}>
                                        <Grid item>
                                            <LoadingButton variant="outlined" color="error" >
                                                {t('chatbots.delete')}
                                            </LoadingButton>
                                        </Grid>
                                    </Tooltip>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
                
            </Grid>
                         
            <SettingsModal
                open={settingsOpen}
                onClose={() => setsettingsOpen(false)}
                onBotChange={setBot}
                chatbot={bot} 
            />
             
            <UserSettingsModal
                open={userSettingsOpen}
                onClose={() => setUserSettingsOpen(false)}
                onBotChange={setBot}
                chatbot={bot} 
            />
            
            <AgentSkillMarketplaceModal
                open={marketplaceOpen}
                onClose={() => {
                    setMarketplaceOpen(false);
                }}
                onSave={handleSaveAgentSkill}
                botId={bot.id}
            />

            {selectedSkill && (
                <AgentSkillSettingsModal
                    open={settingsModalOpen}
                    onClose={() => {
                        setSettingsModalOpen(false);
                        setSelectedSkill(null);
                    }}
                    onSave={handleUpdateAgentSkill}
                    skill={selectedSkill}
                    botId={bot.id}
                />
            )}
            
        </>
    );
}

export default MyChatBot;